import SingleInput from "@/components/controls/SingleInput";
import {
  FieldConfig,
  FieldItem,
  FieldsConfig
} from "@/components/layouts/DetailFields";
import Text from "@/components/ui/text";
import { Commercials } from "@/pages/deals/types";
import { cn } from "@/utils/utils";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import IconContainer from "@/components/ui/icon-container";
import { Error, InfoCircle } from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  createGetFieldKey,
  getFieldValue,
  handleFieldError
} from "./ContractTerms/ContractTerms";
import {
  monetaryValueFormatter,
  monetaryValueFormatterWithDecimals
} from "@/pages/deals/helpers";
import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";
import { useFinancials } from "@/services/hooks/useFinancials";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { reducePrecision } from "@/utils/numbers";
const TRANSLATION_PREFIX = "deals.commercials";
const MONTHS_IN_YEAR = 12;

interface Props {
  data: Commercials | null;
  onUpdateField?: (
    field: string,
    newValue: string | number | Object | null,
    additionalFields?: { [fieldName: string]: string | number | null }
  ) => void;
  containerClassName?: string;
  isReadOnly?: boolean;
  convertedAnnualContractValue?: number | null;
  convertedTotalContractValue?: number | null;
}

const Financials: React.FC<Props> = ({
  data,
  onUpdateField,
  containerClassName,
  isReadOnly = false,
  convertedAnnualContractValue,
  convertedTotalContractValue
}) => {
  const { t } = useTranslation();
  const { isArr } = useArrMrr();
  const {
    accountCurrencySymbol,
    contractCurrencySymbol,
    rate,
    showConversion
  } = useFinancials(data);

  const getFieldKey = useCallback(createGetFieldKey(data), [
    data?.id,
    data?.arrLocal,
    data?.mrrLocal
  ]);

  const [fieldErrors, setFieldErrors] = useState<{
    [fieldName: string]: string;
  }>({});
  const errorIdPrefix = useId();
  const getErrorId = (fieldName: string) => `${errorIdPrefix}-${fieldName}`;

  const arrOrMrrOriginalValue = (field: "mrr" | "arr") => {
    const arrValue = data?.arr ?? 0;
    const mrrValue = data?.mrr ?? 0;

    if (field === "mrr") {
      return isArr
        ? reducePrecision(arrValue / MONTHS_IN_YEAR)
        : mrrValue > 0
          ? mrrValue
          : reducePrecision(arrValue / MONTHS_IN_YEAR);
    } else {
      return isArr
        ? arrValue > 0
          ? arrValue
          : mrrValue * MONTHS_IN_YEAR
        : mrrValue * MONTHS_IN_YEAR;
    }
  };

  const ArrMrrConversion: React.FC<{ amount: number }> = ({ amount }) => {
    return (
      <Text variant="bodySecondary">
        {`(${monetaryValueFormatterWithDecimals(amount, accountCurrencySymbol)})`}
      </Text>
    );
  };

  // Calculate MRR and ARR values - always derive one from the other
  // When isArr is true: ARR is the source, MRR = ARR / 12
  // When isArr is false: MRR is the source, ARR = MRR * 12
  const arrLocalValue = data?.arrLocal ?? 0;
  const mrrLocalValue = data?.mrrLocal ?? 0;

  const calculatedMrrValue = isArr
    ? reducePrecision(arrLocalValue / MONTHS_IN_YEAR)
    : mrrLocalValue > 0
      ? mrrLocalValue
      : reducePrecision(arrLocalValue / MONTHS_IN_YEAR);

  const calculatedArrValue = isArr
    ? arrLocalValue > 0
      ? arrLocalValue
      : mrrLocalValue * MONTHS_IN_YEAR
    : mrrLocalValue * MONTHS_IN_YEAR;

  const fieldsConfig: FieldsConfig = {
    mrr: {
      suffixComponent:
        showConversion &&
        ((data?.arrLocal && data?.arrLocal > 0) ||
          (data?.mrrLocal && data?.mrrLocal > 0)) ? (
          <ArrMrrConversion amount={arrOrMrrOriginalValue("mrr")} />
        ) : undefined,
      ...(isReadOnly || isArr
        ? {
            type: "readOnlyText",
            formatter: () =>
              monetaryValueFormatterWithDecimals(
                calculatedMrrValue,
                contractCurrencySymbol
              )
          }
        : {
            type: "editableInput",
            isMonetary: true,
            currencySymbol: contractCurrencySymbol,
            initialValue: calculatedMrrValue
          })
    },
    arr: {
      suffixComponent:
        showConversion &&
        ((data?.arrLocal && data?.arrLocal > 0) ||
          (data?.mrrLocal && data?.mrrLocal > 0)) ? (
          <ArrMrrConversion amount={arrOrMrrOriginalValue("arr")} />
        ) : undefined,
      ...(isReadOnly || !isArr
        ? {
            type: "readOnlyText",
            formatter: () =>
              monetaryValueFormatterWithDecimals(
                calculatedArrValue,
                contractCurrencySymbol
              )
          }
        : {
            type: "editableInput",
            isMonetary: true,
            currencySymbol: contractCurrencySymbol,
            initialValue: calculatedArrValue
          })
    },
    annualContractValue: {
      suffixComponent:
        showConversion &&
        data?.annualContractValue &&
        data?.annualContractValue > 0 ? (
          convertedAnnualContractValue != null ? (
            <Text variant="bodySecondary">
              {`(${monetaryValueFormatter(convertedAnnualContractValue, accountCurrencySymbol)})`}
            </Text>
          ) : (
            <ConvertedCurrency
              amount={data?.annualContractValue}
              rate={rate}
              accountCurrencySymbol={accountCurrencySymbol}
            />
          )
        ) : undefined,
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: () =>
              monetaryValueFormatter(
                data?.annualContractValue || 0,
                contractCurrencySymbol
              )
          }
        : {
            type: "editableInput",
            isMonetary: true,
            currencySymbol: contractCurrencySymbol,
            initialValue: data?.annualContractValue || 0
          })
    },
    totalContractValue: {
      suffixComponent:
        showConversion &&
        data?.totalContractValue &&
        data?.totalContractValue > 0 ? (
          convertedTotalContractValue != null ? (
            <Text variant="bodySecondary">
              {`(${monetaryValueFormatter(convertedTotalContractValue, accountCurrencySymbol)})`}
            </Text>
          ) : (
            <ConvertedCurrency
              amount={data?.totalContractValue}
              rate={rate}
              accountCurrencySymbol={accountCurrencySymbol}
            />
          )
        ) : undefined,
      ...(isReadOnly
        ? {
            type: "readOnlyText",
            formatter: () =>
              monetaryValueFormatter(
                data?.totalContractValue || 0,
                contractCurrencySymbol
              )
          }
        : {
            type: "editableInput",
            isMonetary: true,
            currencySymbol: contractCurrencySymbol,
            initialValue: data?.totalContractValue || 0
          })
    }
  };

  useEffect(() => {
    setFieldErrors({});
  }, [data?.id]);

  const renderFieldComponent = (
    fieldName: keyof Commercials,
    config: FieldConfig
  ) => {
    const isArrOrMrrField = fieldName === "arr" || fieldName === "mrr";
    const configInitialValue =
      "initialValue" in config ? config.initialValue : undefined;
    const value = config?.selectedValue
      ? config?.selectedValue
      : isArrOrMrrField
        ? configInitialValue
        : getFieldValue(data, fieldName);

    const getFieldNameForSave = (field: keyof Commercials): string => {
      if (field === "arr") return "arrLocal";
      if (field === "mrr") return "mrrLocal";
      return field;
    };

    let switchComponent;

    switch (config.type) {
      case "editableInput":
        switchComponent = (
          <SingleInput
            key={getFieldKey(fieldName)}
            fieldName={fieldName}
            initialValue={
              config.initialValue?.toString() ?? value?.toString() ?? ""
            }
            onSave={(newValue: string) =>
              onUpdateField?.(
                getFieldNameForSave(fieldName),
                isNaN(Number(newValue)) ? newValue : Number(newValue)
              )
            }
            textVariant="body"
            validation={config.validation}
            isMonetary={config.isMonetary}
            isPretty={config.isPretty}
            onError={(error) =>
              handleFieldError(
                setFieldErrors,
                fieldName,
                !!error,
                error || undefined
              )
            }
            externalErrorId={getErrorId(fieldName)}
            currencySymbol={config?.currencySymbol}
            {...config.additionalProps}
            width="auto"
            className="max-w-[75%]"
          />
        );
        break;

      case "readOnlyText":
        switchComponent = (
          <div className="ml-2">
            <Text key={getFieldKey(fieldName)} variant="body">
              {config.formatter
                ? config.formatter(value)
                : (value ?? t("unknown"))}
            </Text>
          </div>
        );
        break;

      default:
        switchComponent = null;
    }

    return (
      <div className="flex items-center w-full">
        {switchComponent}
        {config?.suffixComponent && config.suffixComponent}
      </div>
    );
  };

  const topFieldNames = useMemo(() => (isArr ? ["arr"] : ["mrr"]), [isArr]);
  const bottomFieldNames = ["annualContractValue", "totalContractValue"];

  const getOrderedFieldItemsForGroup = useCallback(
    (fieldGroup: string[]) => {
      const fieldItems: Record<string, FieldItem> = {};
      for (const fieldName of fieldGroup) {
        if (fieldsConfig[fieldName]) {
          fieldItems[fieldName] = {
            component: renderFieldComponent(
              fieldName as keyof Commercials,
              fieldsConfig[fieldName]
            ),
            icon: fieldsConfig[fieldName].icon || ""
          };
        }
      }

      return fieldItems;
    },
    [fieldsConfig]
  );

  const renderField = (fieldName: string, fieldItem: FieldItem) => (
    <div className="group contents">
      <div
        className={cn(
          "flex gap-2 overflow-hidden pr-4 h-9",
          fieldErrors[fieldName] ? "items-start pt-2" : "items-center"
        )}
      >
        <Text variant="bodySecondary" className="truncate" hasTruncatedTooltip>
          {fieldsConfig[fieldName].label ||
            t(`${TRANSLATION_PREFIX}.${fieldName}`)}
        </Text>
        {fieldsConfig[fieldName]?.infoTooltip && (
          <Tooltip>
            <TooltipTrigger>
              <IconContainer color="secondary">
                <InfoCircle />
              </IconContainer>
            </TooltipTrigger>
            <TooltipContent maxWidth={18}>
              {fieldsConfig[fieldName]?.infoTooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex items-center overflow-hidden break-words h-9">
        {fieldItem.component}
      </div>
    </div>
  );

  const renderFieldsContainer = (
    fields: Record<string, FieldItem>,
    fieldOrder: string[],
    additionalClasses?: string
  ) => (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,50%),1fr] gap-y-1",
        containerClassName,
        additionalClasses
      )}
    >
      {fieldOrder.map(
        (fieldName) =>
          fields[fieldName] && (
            <React.Fragment key={fieldName}>
              {renderField(fieldName, fields[fieldName])}
              {fieldErrors[fieldName] && (
                <div className="col-span-2 pt-1 flex gap-1">
                  <IconContainer color="error">
                    <Error />
                  </IconContainer>
                  <Text
                    id={getErrorId(fieldName)}
                    role="alert"
                    variant="smBodyError"
                  >
                    {fieldErrors[fieldName]}
                  </Text>
                </div>
              )}
              {fieldsConfig[fieldName]?.followingComponent}
            </React.Fragment>
          )
      )}
    </div>
  );

  const topFields = useMemo(
    () => getOrderedFieldItemsForGroup(topFieldNames),
    [getOrderedFieldItemsForGroup, topFieldNames]
  );

  const bottomFields = useMemo(
    () => getOrderedFieldItemsForGroup(bottomFieldNames),
    [getOrderedFieldItemsForGroup, bottomFieldNames]
  );

  return (
    <div className="flex flex-col">
      {renderFieldsContainer(topFields, topFieldNames)}
      {renderFieldsContainer(bottomFields, bottomFieldNames)}
    </div>
  );
};

export default Financials;
